import { hideOnError } from "@/lib/img-fallback";
import { BausteineWahlerfolg } from "@/components/wahlen/BausteineWahlerfolg";
import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { lazySection } from "@/lib/lazy-section";
import {
  testimonialsQueryOptions,
  casesByTagQueryOptions,
  leadMagnetTeaserQueryOptions,
} from "@/lib/public-content.queries";
import { VoteBoxAnimation } from "@/components/buergermeisterwahl/VoteBoxAnimation";
import { ScrollRevealText } from "@/components/ScrollRevealText";

const TestimonialsSlider = lazySection(async () => {
  const m = await import("@/components/home/TestimonialsSlider");
  return { default: m.TestimonialsSlider };
}, { minHeight: 520 });
const CasesSection = lazySection(async () => {
  const m = await import("@/components/cases/CasesSection");
  return { default: m.CasesSection };
}, { minHeight: 480 });
const ContactCta = lazySection(async () => {
  const m = await import("@/components/direktkandidaten/ContactCta");
  return { default: m.ContactCta };
}, { minHeight: 400 });
// Freebie-Abschnitt (Ticket W14-Baustein), unterhalb des Sichtfelds.
const FreebieTeaserSection = lazySection(async () => {
  const m = await import("@/components/freebie/FreebieTeaserSection");
  return { default: m.FreebieTeaserSection };
}, { minHeight: 320 });
const ProzessTimeline = lazySection(async () => {
  const m = await import("@/components/buergermeisterwahl/ProzessTimeline");
  return { default: m.ProzessTimeline };
}, { minHeight: 600 });
import einzigartigeErfahrungAsset from "@/assets/buergermeisterwahl/einzigartige-erfahrung.png.asset.json";
import direktmandatZuUnsAsset from "@/assets/direktkandidaten/direktmandat-zu-uns.png.asset.json";
import keineLieblingsparteiAsset from "@/assets/direktkandidaten/demokratie.png.asset.json";
import modularUndFairAsset from "@/assets/buergermeisterwahl/modular-und-fair.png.asset.json";
import sicherheitDurchErfahrungAsset from "@/assets/buergermeisterwahl/sicherheit-durch-erfahrung.png.asset.json";
import { BookingCtaButton } from "@/components/cta/BookingCtaButton";


export const Route = createFileRoute("/wahlen-gewinnen/direktkandidaten")({
  // Ticket W7: Herkunftskennzeichnung vom Verteiler /wahlkampfagentur (?von=...).
  validateSearch: (search: Record<string, unknown>): { von?: string } => ({
    von: typeof search.von === "string" ? search.von : undefined,
  }),
  // Testimonials und Cases speisen nur lazy nachgeladene Below-the-Fold-Sektionen
  // → Prefetch anstoßen, aber nicht awaiten (kürzere TTFB / kritische Kette).
  loader: async ({ context }) => {
    void context.queryClient.prefetchQuery(testimonialsQueryOptions);
    void context.queryClient.prefetchQuery(casesByTagQueryOptions("direktkandidaten"));
    void context.queryClient.prefetchQuery(leadMagnetTeaserQueryOptions("wahlslogan-beispiele"));
  },
  head: () => {
    const title = "Wahlkampf für Direktkandidaten | creategy";
    const description =
      "Wahlkampf für Direktkandidaten zu Landtag und Bundestag: Personenmarke, Strategie und Kampagne, die im Wahlkreis überzeugt. Jetzt Erstgespräch vereinbaren.";
    const url = "https://creategy.de/wahlen-gewinnen/direktkandidaten";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "creategy" },
        { property: "og:locale", content: "de_DE" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: DirektkandidatenPage,
});

function DirektkandidatenPage() {
  const gruende = [
    {
      title: "Sicherheit durch Erfahrung.",
      text: "Über 200 Wahlkämpfe, 72,6 Prozent Erfolgsquote, kein Auftrag, der nicht politisch ist. Wer zu uns kommt, bekommt kein Team, das sich einarbeitet, sondern eines das schon da war.",
    },
    {
      title: "Modular und fair.",
      text: "Sie buchen nur, was Sie brauchen. Kein aufgeblähtes Paket, keine versteckten Kosten, kein Upselling im falschen Moment. Transparentes Pricing.",
    },
    {
      title: "Wir haben keine Lieblingspartei.",
      text: "Aber wir haben eine Lieblingsidee: Demokratie. Wir arbeiten für alle, die demokratisch antreten, egal welche Farbe. Aber für niemanden, der den Wettbewerb selbst abschaffen will.",
    },
    {
      title: "Einzigartige Erfahrung.",
      text: "Unser Senior Advisor war Regierungssprecher und Staatssekretär a.D., weiß wie Kommunikation in den schwierigsten Momenten funktioniert und gibt dieses Wissen direkt an unsere Kandidatinnen und Kandidaten weiter.",
    },
    {
      title: "Ihr Direktmandat zu uns.",
      text: 'Kein Ticketsystem, keine Warteschleife, kein „Ich leite das weiter." Bei creategy haben Sie einen festen Ansprechpartner: erreichbar per Telefon, Mail und WhatsApp.',
    },
  ];

  const gruendeImages = [
    { src: sicherheitDurchErfahrungAsset.url, alt: "Strategieentwicklung auf Whiteboard als Symbol für Erfahrung" },
    { src: modularUndFairAsset.url, alt: "Gelber Baustein wird auf eine graue Mauer gesetzt – Symbol für modulare Bausteine" },
    { src: keineLieblingsparteiAsset.url, alt: "Stimmzettel und Briefwahlumschlag – Symbol für parteiliche Unabhängigkeit" },
    { src: einzigartigeErfahrungAsset.url, alt: "Senior Advisor Michael Bußer im Gespräch" },
    { src: direktmandatZuUnsAsset.url, alt: "Direktkandidat im Telefonat – Kontaktaufnahme mit creategy" },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section aria-labelledby="hero">
        <div className="section-y-hero mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div className="fade-in-up">
              <h1
                id="hero"
                className="heading-h1 max-w-5xl text-brand-wahlen"
              >
                Persönlichkeit schlägt Parteilinie.
              </h1>
              <p
                className="mt-10 max-w-2xl font-body text-base leading-relaxed text-foreground md:text-lg"
                style={{ animationDelay: "160ms" }}
              >
                creategy entwickelt Kampagnen für Kandidatinnen und Kandidaten, die
                im Wahlkreis überzeugen müssen mit Profil, klarer Botschaft und einer
                Mobilisierung, die bis an die Haustür reicht.
              </p>
              <BookingCtaButton
                variant="primary"
                eventPath="creategy/erstgespraech-dk"
                className="mt-10 max-w-max"
                style={{ animationDelay: "240ms" }}
              />
            </div>
            <div className="order-first md:order-none w-full max-w-[240px] sm:max-w-[280px] md:max-w-[360px] mx-auto md:ml-auto md:mr-0 lg:max-w-[420px]">
              <VoteBoxAnimation />
            </div>
          </div>
        </div>
      </section>

      {/* Kennzahlen */}
      <section aria-labelledby="kennzahlen">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <dl className="grid gap-10 sm:grid-cols-2 md:grid-cols-3">

            <div className="flex flex-col">
              <dd className="font-display text-5xl font-extrabold leading-none text-brand-wahlen md:text-6xl">
                <CountUp end={72.6} decimals={1} suffix=" %" />
              </dd>
              <dt className="mt-4 max-w-xs font-body text-sm leading-snug text-foreground">
                Erfolgsquote bei Direktwahlen.
              </dt>
            </div>
            <div className="flex flex-col">
              <dd className="font-display text-5xl font-extrabold leading-none text-brand-wahlen md:text-6xl">
                <CountUp end={200} suffix="+" />
              </dd>
              <dt className="mt-4 max-w-xs font-body text-sm leading-snug text-foreground">
                Betreute Wahlkämpfe in der ganzen Republik.
              </dt>
            </div>
            <div className="flex flex-col">
              <dd className="font-display text-5xl font-extrabold leading-none text-brand-wahlen md:text-6xl">
                <CountUp end={7} />
              </dd>
              <dt className="mt-4 max-w-xs font-body text-sm leading-snug text-foreground">
                Tage Express-Setup.
              </dt>
            </div>
          </dl>
        </div>
      </section>

      {/* Mandatsebenen */}
      <section aria-labelledby="mandatsebenen">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <ScrollRevealText
            text="Kandidaten für das Europäische Parlament ‧ Bundestagskandidaten ‧ Landtagskandidaten ‧ Kandidaten für das Abgeordnetenhaus ‧ Kandidaten für Regionalparlamente ‧ Landratskandidaten ‧ Kandidaten für den Kreistag ‧ Kandidaten für den Gemeinderat"
            className="max-w-none font-body text-xl font-normal leading-snug text-primary hyphens-none md:text-2xl lg:text-3xl"
          />
        </div>
      </section>



      {/* Bausteine */}
      <BausteineWahlerfolg />

      {/* Testimonials */}
      <TestimonialsSlider
        showFilters={false}
        fixedCategory="direktkandidaten"
        title="Das sagen unsere Wahlsieger."
        headingColor="text-brand-wahlen"
      />





      {/* 5 Gründe für creategy – Sticky-Stack der Karten, Bild links parallel sticky */}
      <section
        aria-labelledby="gruende"
        className="relative z-0 isolate bg-background text-foreground"
      >
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="mb-6 md:mb-10">
            <Reveal>
              <h2 id="gruende" className="heading-h2 text-brand-wahlen">
                5 Gründe{" "}
                für creategy
              </h2>
            </Reveal>
          </div>

          <ol className="flex flex-col gap-4 md:gap-0">
            {gruende.map((card, i) => {
              const img = gruendeImages[i];
              return (
                <li
                  key={card.title}
                  className="md:sticky md:top-32 md:pb-[6.66vh]"
                  style={{ zIndex: 10 + i }}
                >
                  <article className="grid gap-8 rounded border border-brand-wahlen/20 bg-background p-7 shadow-[0_10px_30px_-12px_rgba(0,75,81,0.18)] md:min-h-[28rem] md:grid-cols-[minmax(0,1.5fr)_minmax(0,3.5fr)] md:items-stretch md:gap-8 md:p-9 lg:min-h-0">
                    <div className="relative aspect-square w-full max-w-[360px] overflow-hidden rounded border border-brand-wahlen/20 bg-brand-wahlen/5">
                      <img
                        src={img.src}
                        alt={img.alt}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async" onError={hideOnError}
                      />
                    </div>
                    <div className="flex h-full flex-col justify-center">
                      <div>
                        <h3 className="heading-h3 text-brand-wahlen">
                          {card.title}
                        </h3>
                        <p className="mt-4 font-body text-base leading-relaxed text-foreground/85">
                          {card.text}
                        </p>
                      </div>
                      <div className="mt-6 font-display text-sm font-extrabold tracking-wider text-brand-wahlen/60">
                        Grund {i + 1} von {gruende.length}
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Freebie: 100 Claims – gleicher Baustein wie auf der Bürgermeisterwahlseite. */}
      <FreebieTeaserSection
        slug="wahlslogan-beispiele"
        kennung="direktkandidaten-claims"
        accentClass="text-brand-wahlen"
        heading="Kostenlos: 100 Claims für Ihren Wahlkampf."
        text="Ein guter Claim bringt Ihre Kandidatur auf den Punkt. Hundert Beispiele aus echten Wahlkämpfen, sortiert nach Ausgangslage, dazu eine Anleitung, wie Sie daraus Ihren eigenen Claim entwickeln. Als PDF zum Herunterladen, ohne Kosten, ohne Verpflichtung."
      />

      {/* Cases */}
      <CasesSection tag="direktkandidaten" headingColor="text-brand-wahlen" />

      {/* Prozess */}
      <section aria-labelledby="prozess">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="prozess" className="heading-h2 text-brand-wahlen">
              In drei Schritten{" "}
              zur Zusammenarbeit.
            </h2>
          </Reveal>

          <div className="mt-16 md:mt-20">
            <ProzessTimeline headingColor="text-brand-wahlen" />
          </div>

        </div>
      </section>
      <ContactCta />


    </Layout>
  );
}
